import { LOCAL_STORAGE_NAMES } from '../constants/constants';
import { readUserPreference, storeUserPreference } from './userPreferences';

export type DisplaySize = 'small' | 'normal' | 'large';

export const DISPLAY_SIZES: readonly DisplaySize[] = ['small', 'normal', 'large'];

export const DEFAULT_DISPLAY_SIZE: DisplaySize = 'normal';

export const DISPLAY_SIZE_LABEL_KEYS: Record<DisplaySize, string> = {
  small: 'common:uiScale.small',
  normal: 'common:uiScale.normal',
  large: 'common:uiScale.large',
};

export const DISPLAY_SIZE_ZOOM: Record<DisplaySize, number> = {
  small: 0.9,
  normal: 1,
  large: 1.1,
};

interface IEngineZoomScales {
  viewportUnitScale: number;
  clientScale: number;
}

const UNSCALED: IEngineZoomScales = { viewportUnitScale: 1, clientScale: 1 };

let appliedSize: DisplaySize = DEFAULT_DISPLAY_SIZE;
let appliedZoom = 1;
let appliedScales = UNSCALED;

export const isDisplaySize = (value: unknown): value is DisplaySize =>
  DISPLAY_SIZES.includes(value as DisplaySize);

export const readStoredDisplaySize = (userId?: string): DisplaySize =>
  readUserPreference(LOCAL_STORAGE_NAMES.displaySize, userId, isDisplaySize, DEFAULT_DISPLAY_SIZE);

export const storeDisplaySize = (userId: string, size: DisplaySize): void =>
  storeUserPreference(LOCAL_STORAGE_NAMES.displaySize, userId, size);

// An engine either applies the root zoom to a measurement or it doesn't, so rounding noise snaps to one of the two.
const snapToZoom = (ratio: number, zoom: number): number =>
  Math.abs(ratio - zoom) < Math.abs(ratio - 1) ? zoom : 1;

// Chromium scales 100vw and client coordinates by the root zoom while WebKit already reports both in zoomed pixels.
const measureEngineZoomScales = (zoom: number): IEngineZoomScales | undefined => {
  if (zoom === 1) return UNSCALED;
  const viewportWidth = globalThis.innerWidth;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:100vw;height:0;visibility:hidden;pointer-events:none';
  document.body.append(probe);
  const widthInCssPixels = probe.offsetWidth;
  const widthInClientPixels = probe.getBoundingClientRect().width;
  probe.remove();
  // A pane with no width yet (hidden or still opening) can't tell the two engines apart.
  if (viewportWidth === 0 || widthInCssPixels === 0) return undefined;
  return {
    viewportUnitScale: snapToZoom(widthInCssPixels / (viewportWidth / zoom), zoom),
    clientScale: snapToZoom(widthInClientPixels / widthInCssPixels, zoom),
  };
};

const reapplyDisplaySize = () => applyDisplaySize(appliedSize);

const ROOT_CLIENT_SIZES = ['clientWidth', 'clientHeight'] as const;

// WebKit reports the root's client size unzoomed while its rects are zoomed, so antd would place popups past the pane edge.
const alignRootClientSize = (scale: number): void => {
  const root = document.documentElement;
  ROOT_CLIENT_SIZES.forEach((property) => {
    Reflect.deleteProperty(root, property);
    const readNativeSize = Object.getOwnPropertyDescriptor(Element.prototype, property)?.get;
    if (scale === 1 || !readNativeSize) return;
    Object.defineProperty(root, property, {
      configurable: true,
      get: () => readNativeSize.call(root) / scale,
    });
  });
};

export const applyDisplaySize = (size: DisplaySize): void => {
  const root = document.documentElement;
  appliedSize = size;
  appliedZoom = DISPLAY_SIZE_ZOOM[size];
  root.dataset.displaySize = size;
  root.style.zoom = String(appliedZoom);
  const measuredScales = measureEngineZoomScales(appliedZoom);
  if (!measuredScales) globalThis.addEventListener('resize', reapplyDisplaySize, { once: true });
  appliedScales = measuredScales ?? UNSCALED;
  root.style.setProperty('--viewport-unit-scale', String(appliedScales.viewportUnitScale));
  alignRootClientSize(appliedZoom / appliedScales.clientScale);
};

export const clientToCssPixels = (clientPixels: number): number =>
  clientPixels / appliedScales.clientScale;

export const readViewportCssSize = (): { width: number; height: number } => ({
  width: globalThis.innerWidth / appliedZoom,
  height: globalThis.innerHeight / appliedZoom,
});
